import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CategorySelection, RegisterUserForm } from '../interfaces';
import { VisitorRegistrationBeforeModularFormField, TripType } from '../interfaces/register-user.response';
import { minArrayLengthValidator } from '../shared/form/validators';
import { RestService } from '../../../services/rest.service';
import { ToolsService } from '../../../services/tools.service';
import { SEARCH_RADIUS_DEFAULT_VALUE } from '../shared/components/search-radius-field/config/constants';
import { DEFAULT_TIME } from '../shared/components/time-fields/components/time-field/config/constants';
import { InformerFormPolicy } from './informer-form-policy.service';

@Injectable({
  providedIn: 'root'
})
export class RegisterUserFormFactoryService {
  private readonly _fb = inject(FormBuilder);
  private readonly _restService = inject(RestService);
  private readonly _policy = inject(InformerFormPolicy);
  private readonly _toolsService = inject(ToolsService);

  /**
   * HRS-2990: los campos obligatorios los decide `InformerFormPolicy` según el
   * modo del informador, no un booleano de modo técnico. El llamador sigue
   * recreando el formulario cuando cambian modo o token (ver el effect de
   * RegisterUserComponent).
   */
  public createForm(
    modularFields?: VisitorRegistrationBeforeModularFormField[],
    recommendedRouteToken = null,
  ): FormGroup {
    // El registro simple no pinta la hora (`showStartTime`): un required invisible
    // solo podría bloquear el envío sin decir por qué.
    const startTimeValidators = recommendedRouteToken || !this._policy.showStartTime()
      ? []
      : [Validators.required];
    const tripTypeValidators = this._policy.requireTripTypes() ? [Validators.required] : [];
    const categoriesValidators = this._policy.requireInterests()
      ? [Validators.required, minArrayLengthValidator(this._policy.minInterests())]
      : [];

    const form = this._fb.group<RegisterUserForm>({
      startDate: new FormControl<Date | null>(null, Validators.required),
      endDate: new FormControl<Date | null>(null, Validators.required),
      startTime: new FormControl<string | null>(DEFAULT_TIME, startTimeValidators),
      startLocationAddress: new FormControl<string>(null),
      startLocationLat: new FormControl<number>(null),
      startLocationLng: new FormControl<number>(null),
      tripType: new FormControl<TripType>(null, tripTypeValidators),
      categories: new FormControl<CategorySelection[]>(null, categoriesValidators),
      subdomain: new FormControl<string>(null, Validators.required),
      search_radius_km: new FormControl<number | null>(this._initialSearchRadiusKm()),
    });

    // Privacy of the recommended route being created (predefined-route only).
    // false = 'Público' (listed), true = 'Solo QR' (token/QR access only).
    if (this._policy.showPrivacy()) {
      (form as FormGroup).addControl('privacy', this._fb.control<boolean>(false));
    }

    if (this._policy.showModularFields() && modularFields?.length) {
      modularFields.forEach((field, index) => {
        const key = `${field.type}_${index}`;
        const validators = field.required ? [Validators.required] : [];
        (form as FormGroup).addControl(key, this._fb.control(null, validators));
      });
    }

    this._setSubdomain(form);
    this._setStartLocation(form);

    return form;
  }

  /**
   * HRS-2579: el radio con el que sale marcado el campo lo decide el CMS por app
   * (`maxRouteRadius.defaultKm` de getCommonApp). Se lee aquí dentro a propósito:
   * el efecto que crea el formulario rastrea la señal y lo rehace si el CMS
   * contesta después.
   *
   * `defaultKm` a null es "sin límite" y hay que respetarlo, así que no vale un
   * `??`: la constante solo cubre el caso de que no haya bloque del CMS.
   */
  private _initialSearchRadiusKm(): number | null {
    const maxRouteRadius = this._toolsService.maxRouteRadius();

    return maxRouteRadius ? maxRouteRadius.defaultKm : SEARCH_RADIUS_DEFAULT_VALUE;
  }

  private _setSubdomain(form: FormGroup): void {
    const subdomain = this._restService.subDomain;

    form.get('subdomain')?.setValue(subdomain || 'default-subdomain');
  }

  private _setStartLocation(form: FormGroup): void {
    const location = this._restService.device()?.location;
    if (!location) return;

    if (location.address) {
      form.get('startLocationAddress')?.setValue(location.address);
    }
    if (location.latitude) {
      form.get('startLocationLat')?.setValue(location.latitude);
    }
    if (location.longitude) {
      form.get('startLocationLng')?.setValue(location.longitude);
    }
  }
}
